import { useRef, useState } from "react";
import { Paperclip, UploadCloud, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MAX_FILES = 10;
const MAX_MB = 25;
const ACCEPT = ".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.heic,.webp,.zip,.doc,.docx,.xls,.xlsx";
const fmt = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const FileRow = ({ item, onRemove, i }) => (
  <li data-testid={`attachment-item-${i}`} className="flex items-center gap-3 bg-white border border-ink/15 px-4 py-3">
    {item.status === "uploading" ? <Loader2 className="h-4 w-4 text-cobalt animate-spin shrink-0" />
      : item.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
      : <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold truncate">{item.name}</p>
      <p className="text-xs text-slate-500">{item.status === "uploading" ? `Uploading… ${item.progress}%` : item.status === "error" ? item.error : fmt(item.size)}</p>
    </div>
    <button type="button" onClick={() => onRemove(item.key)} data-testid={`attachment-remove-${i}`} aria-label={`Remove ${item.name}`} className="p-1.5 text-slate-500 hover:text-red-600 transition-colors"><X className="h-4 w-4" /></button>
  </li>
);

export const AttachmentsField = ({ files, setFiles }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const patch = (key, p) => setFiles((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const upload = async (file, key) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { onUploadProgress: (e) => patch(key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) });
      patch(key, { status: "done", id: data?.id });
    } catch (err) {
      patch(key, { status: "error", error: formatApiError(err) });
    }
  };

  const add = (list) => {
    const room = MAX_FILES - files.length;
    const picked = Array.from(list || []).slice(0, Math.max(0, room));
    if (list?.length > room) toast.error(`You can attach up to ${MAX_FILES} files`);
    picked.forEach((file) => {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      if (file.size > MAX_MB * 1048576) return toast.error(`${file.name} is larger than ${MAX_MB} MB`);
      setFiles((l) => [...l, { key, name: file.name, size: file.size, status: "uploading", progress: 0 }]);
      upload(file, key);
    });
  };

  return (
    <div data-testid="attachments-field">
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center text-center gap-3 border-2 border-dashed px-6 py-10 cursor-pointer transition-colors ${drag ? "border-cobalt bg-cobalt/5" : "border-ink/20 bg-white hover:border-cobalt"}`}>
        <UploadCloud className="h-8 w-8 text-cobalt" />
        <span className="font-bold">Drop photos or plans here, or <span className="text-cobalt underline">browse files</span></span>
        <span className="text-xs text-slate-500 max-w-md">Photos of the job, or architectural, stormwater, structural, BASIX and sewer plans — images, PDF, DWG, DXF, Word, Excel or ZIP. Up to {MAX_FILES} files, {MAX_MB} MB each.</span>
        <input ref={input} type="file" multiple accept={ACCEPT} data-testid="attachments-input" className="sr-only"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </label>
      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((item, i) => <FileRow key={item.key} item={item} i={i} onRemove={(k) => setFiles((l) => l.filter((x) => x.key !== k))} />)}
        </ul>
      )}
      {files.length > 0 && <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Paperclip className="h-3.5 w-3.5" />{files.filter((x) => x.status === "done").length} of {files.length} attached</p>}
    </div>
  );
};
